<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import Integration from './Integration.vue';
import Spinner from 'shared/components/Spinner.vue';
import integrationAPI from 'dashboard/api/integrations';

import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const store = useStore();
const dialogRef = ref(null);
const integrationLoaded = ref(false);
const storeUrl = ref('');
const consumerKey = ref('');
const consumerSecret = ref('');
const isSubmitting = ref(false);
const formError = ref('');

// Sync status state
const syncStatus = ref(null);
const syncStatusLoading = ref(false);
const syncStatusError = ref('');
const isTriggeringSyncState = ref(false);

// Sync history state
const syncHistory = ref([]);
const syncHistoryLoading = ref(false);
const syncHistoryError = ref('');
const integration = useFunctionGetter(
  'integrations/getIntegration',
  'woocommerce'
);
const uiFlags = useMapGetter('integrations/getUIFlags');

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }
  return 'connect';
});

const hideCredentialsModal = () => {
  storeUrl.value = '';
  consumerKey.value = '';
  consumerSecret.value = '';
  formError.value = '';
  isSubmitting.value = false;
};

const validateForm = () => {
  if (!storeUrl.value || !consumerKey.value || !consumerSecret.value) {
    formError.value = 'All fields are required';
    return false;
  }
  try {
    const url = new URL(storeUrl.value);
    if (!url.protocol.startsWith('http')) throw new Error('Invalid protocol');
  } catch {
    formError.value =
      'Please enter a valid store URL (e.g., https://your-store.com)';
    return false;
  }
  return true;
};

const openCredentialsDialog = () => {
  if (dialogRef.value) {
    dialogRef.value.open();
  }
};

const handleCredentialsSubmit = async () => {
  try {
    formError.value = '';
    if (!validateForm()) {
      return;
    }

    isSubmitting.value = true;
    await integrationAPI.connectWooCommerce({
      storeUrl: storeUrl.value,
      consumerKey: consumerKey.value,
      consumerSecret: consumerSecret.value,
    });

    await store.dispatch('integrations/get', 'woocommerce');
    hideCredentialsModal();
    dialogRef.value?.close();
  } catch (error) {
    formError.value =
      error.response?.data?.error ||
      'Failed to connect. Please check your credentials.';
  } finally {
    isSubmitting.value = false;
  }
};

const initializeWooCommerceIntegration = async () => {
  await store.dispatch('integrations/get', 'woocommerce');
  integrationLoaded.value = true;
};

// Sync status functions
const fetchSyncStatus = async () => {
  if (!integration.value.enabled) {
    syncStatus.value = null;
    return;
  }

  syncStatusLoading.value = true;
  syncStatusError.value = '';

  try {
    const response = await integrationAPI.getWooCommerceSyncStatus();
    syncStatus.value = response.data;
  } catch (error) {
    syncStatusError.value =
      error.response?.data?.error || 'Failed to fetch sync status';
    syncStatus.value = null;
  } finally {
    syncStatusLoading.value = false;
  }
};

const fetchSyncHistory = async () => {
  if (!integration.value.enabled) {
    syncHistory.value = [];
    return;
  }

  syncHistoryLoading.value = true;
  syncHistoryError.value = '';

  try {
    const response = await integrationAPI.getWooCommerceSyncHistory(20);
    syncHistory.value = response.data.history || [];
  } catch (error) {
    syncHistoryError.value =
      error.response?.data?.error || 'Failed to fetch sync history';
    syncHistory.value = [];
  } finally {
    syncHistoryLoading.value = false;
  }
};

const triggerSync = async (forceFullSync = false) => {
  isTriggeringSyncState.value = true;

  try {
    await integrationAPI.triggerWooCommerceSync(forceFullSync);
    // Refresh status and history after triggering sync
    setTimeout(() => {
      fetchSyncStatus();
      fetchSyncHistory();
    }, 2000);
  } catch (error) {
    syncStatusError.value =
      error.response?.data?.error || 'Failed to trigger sync';
  } finally {
    isTriggeringSyncState.value = false;
  }
};

const formatDate = dateStr => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString();
};

const formatDuration = durationSeconds => {
  if (!durationSeconds) return '-';
  if (durationSeconds < 60) return `${durationSeconds}s`;
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const getStatusBadgeClass = status => {
  if (status === 'success')
    return 'bg-n-green-alpha-2 text-n-green-11 border border-n-green-6';
  if (status === 'partial')
    return 'bg-n-amber-alpha-2 text-n-amber-11 border border-n-amber-6';
  if (status === 'failed')
    return 'bg-n-ruby-alpha-2 text-n-ruby-11 border border-n-ruby-6';
  return 'bg-n-slate-alpha-2 text-n-slate-11 border border-n-slate-6';
};

const getTriggerTypeLabel = triggerType => {
  if (triggerType === 'manual_full') return 'Manual (Full)';
  if (triggerType === 'incremental') return 'Auto (Incremental)';
  if (triggerType === 'cron') return 'Scheduled';
  return triggerType || 'Unknown';
};

const statusColor = computed(() => {
  if (!syncStatus.value) return 'text-n-slate-11';
  const status = syncStatus.value.lastSyncStatus;
  if (status === 'success') return 'text-n-green-9';
  if (status === 'partial') return 'text-n-amber-9';
  if (status === 'failed') return 'text-n-ruby-9';
  return 'text-n-slate-11';
});

const statusIcon = computed(() => {
  if (!syncStatus.value) return 'i-lucide-circle-help';
  const status = syncStatus.value.lastSyncStatus;
  if (status === 'success') return 'i-lucide-circle-check';
  if (status === 'partial') return 'i-lucide-triangle-alert';
  if (status === 'failed') return 'i-lucide-circle-x';
  return 'i-lucide-circle-help';
});

// Watch for integration enabled changes to fetch sync data
watch(
  () => integration.value.enabled,
  enabled => {
    if (enabled) {
      fetchSyncStatus();
      fetchSyncHistory();
    } else {
      syncStatus.value = null;
      syncHistory.value = [];
    }
  }
);

onMounted(() => {
  initializeWooCommerceIntegration();
  // Fetch sync status and history after integration is loaded
  setTimeout(() => {
    if (integration.value.enabled) {
      fetchSyncStatus();
      fetchSyncHistory();
    }
  }, 500);
});
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto max-w-6xl mx-auto">
    <div
      v-if="integrationLoaded && !uiFlags.isCreatingWooCommerce"
      class="flex flex-col gap-6"
    >
      <Integration
        :integration-id="integration.id"
        :integration-logo="integration.logo"
        :integration-name="integration.name"
        :integration-description="integration.description"
        :integration-enabled="integration.enabled"
        :integration-action="integrationAction"
        :delete-confirmation-text="{
          title: $t('INTEGRATION_SETTINGS.WOOCOMMERCE.DELETE.TITLE'),
          message: $t('INTEGRATION_SETTINGS.WOOCOMMERCE.DELETE.MESSAGE'),
        }"
      >
        <template #action>
          <Button
            teal
            :label="$t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            @click="openCredentialsDialog"
          />
        </template>
      </Integration>

      <!-- Product Embedding Sync Status Panel -->
      <div
        v-if="integration.enabled"
        class="bg-n-alpha-1 border border-n-weak rounded-xl p-6"
      >
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-image" class="text-n-slate-11" />
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TITLE') }}
            </h3>
          </div>
          <div class="flex items-center gap-2">
            <Button
              xs
              slate
              :label="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.REFRESH')"
              :is-loading="syncStatusLoading"
              @click="fetchSyncStatus"
            />
            <Button
              xs
              teal
              :label="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TRIGGER')"
              :is-loading="isTriggeringSyncState"
              :disabled="syncStatus?.isCurrentlySyncing"
              @click="triggerSync(false)"
            />
          </div>
        </div>

        <p class="text-sm text-n-slate-11 mb-4">
          {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.DESCRIPTION') }}
        </p>

        <div v-if="syncStatusLoading" class="flex justify-center py-4">
          <Spinner size="small" color-scheme="primary" />
        </div>

        <div v-else-if="syncStatusError" class="text-sm text-n-ruby-9 py-2">
          {{ syncStatusError }}
        </div>

        <div v-else-if="syncStatus" class="space-y-4">
          <!-- Status Grid -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <!-- Sync Status -->
            <div class="bg-n-alpha-2 rounded-lg p-3">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.STATUS') }}
              </p>
              <div class="flex items-center gap-1.5">
                <Icon :icon="statusIcon" :class="statusColor" />
                <span class="text-sm font-medium" :class="[statusColor]">
                  {{
                    syncStatus.isCurrentlySyncing
                      ? $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.SYNCING')
                      : syncStatus.lastSyncStatus || 'Not synced'
                  }}
                </span>
              </div>
            </div>

            <!-- Products Indexed -->
            <div class="bg-n-alpha-2 rounded-lg p-3">
              <p class="text-xs text-n-slate-11 mb-1">
                {{
                  $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.PRODUCTS_INDEXED')
                }}
              </p>
              <p class="text-sm font-medium text-n-slate-12">
                {{
                  $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.PRODUCTS_COUNT', {
                    indexed: syncStatus.productsIndexed,
                    total: syncStatus.totalProducts,
                  })
                }}
              </p>
            </div>

            <!-- Last Sync -->
            <div class="bg-n-alpha-2 rounded-lg p-3">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.LAST_SYNC') }}
              </p>
              <p class="text-sm font-medium text-n-slate-12">
                {{ formatDate(syncStatus.lastSyncAt) }}
              </p>
            </div>

            <!-- Next Sync -->
            <div class="bg-n-alpha-2 rounded-lg p-3">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.NEXT_SYNC') }}
              </p>
              <p class="text-sm font-medium text-n-slate-12">
                {{ formatDate(syncStatus.nextSyncAt) }}
              </p>
            </div>
          </div>

          <!-- Progress Bar (when syncing) -->
          <div v-if="syncStatus.isCurrentlySyncing" class="mt-4">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs text-n-slate-11">
                {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.PROGRESS') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.PROGRESS_PERCENT', {
                    percent: syncStatus.syncProgress,
                  })
                }}
              </span>
            </div>
            <div class="w-full bg-n-alpha-3 rounded-full h-2">
              <div
                class="bg-n-teal-9 h-2 rounded-full transition-all duration-300"
                :style="{ width: `${syncStatus.syncProgress}%` }"
              />
            </div>
          </div>

          <!-- Last Error (if any) -->
          <div
            v-if="syncStatus.lastError"
            class="bg-n-ruby-alpha-2 border border-n-ruby-6 rounded-lg p-3 mt-4"
          >
            <p class="text-xs text-n-ruby-11 mb-1">
              {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.LAST_ERROR') }}
            </p>
            <p class="text-sm text-n-ruby-9">{{ syncStatus.lastError }}</p>
          </div>
        </div>

        <div v-else class="text-sm text-n-slate-11 py-2">
          {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.NOT_AVAILABLE') }}
        </div>
      </div>

      <!-- Sync History Table -->
      <div
        v-if="integration.enabled"
        class="bg-n-alpha-1 border border-n-weak rounded-xl p-6"
      >
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-history" class="text-n-slate-11" />
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.HISTORY_TITLE') }}
            </h3>
          </div>
          <Button
            xs
            slate
            :label="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.REFRESH')"
            :is-loading="syncHistoryLoading"
            @click="fetchSyncHistory"
          />
        </div>

        <p class="text-sm text-n-slate-11 mb-4">
          {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.HISTORY_DESCRIPTION') }}
        </p>

        <div v-if="syncHistoryLoading" class="flex justify-center py-4">
          <Spinner size="small" color-scheme="primary" />
        </div>

        <div v-else-if="syncHistoryError" class="text-sm text-n-ruby-9 py-2">
          {{ syncHistoryError }}
        </div>

        <div v-else-if="syncHistory.length > 0" class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="border-b border-n-weak">
              <tr class="text-left">
                <th class="pb-3 pr-4 font-medium text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TABLE.DATE') }}
                </th>
                <th class="pb-3 pr-4 font-medium text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TABLE.STATUS') }}
                </th>
                <th class="pb-3 pr-4 font-medium text-n-slate-11">
                  {{
                    $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TABLE.TRIGGER')
                  }}
                </th>
                <th class="pb-3 pr-4 font-medium text-n-slate-11 text-right">
                  {{
                    $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TABLE.UPDATED')
                  }}
                </th>
                <th class="pb-3 pr-4 font-medium text-n-slate-11 text-right">
                  {{
                    $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TABLE.SKIPPED')
                  }}
                </th>
                <th class="pb-3 pr-4 font-medium text-n-slate-11 text-right">
                  {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TABLE.FAILED') }}
                </th>
                <th class="pb-3 font-medium text-n-slate-11 text-right">
                  {{
                    $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.TABLE.DURATION')
                  }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="entry in syncHistory"
                :key="entry.id"
                class="hover:bg-n-alpha-2"
              >
                <td class="py-3 pr-4 text-n-slate-12">
                  {{ formatDate(entry.syncStartedAt) }}
                </td>
                <td class="py-3 pr-4">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="[getStatusBadgeClass(entry.status)]"
                  >
                    {{ entry.status }}
                  </span>
                </td>
                <td class="py-3 pr-4 text-n-slate-12">
                  {{ getTriggerTypeLabel(entry.triggerType) }}
                </td>
                <td class="py-3 pr-4 text-right text-n-slate-12">
                  {{ entry.productsUpdated }}
                </td>
                <td class="py-3 pr-4 text-right text-n-slate-11">
                  {{ entry.productsSkipped }}
                </td>
                <td
                  class="py-3 pr-4 text-right"
                  :class="[
                    entry.productsFailed > 0
                      ? 'text-n-ruby-9'
                      : 'text-n-slate-11',
                  ]"
                >
                  {{ entry.productsFailed }}
                </td>
                <td class="py-3 text-right text-n-slate-11">
                  {{ formatDuration(entry.durationSeconds) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="text-sm text-n-slate-11 py-4 text-center">
          {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SYNC.NO_HISTORY') }}
        </div>
      </div>

      <Dialog
        ref="dialogRef"
        :title="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.TITLE')"
        :is-loading="isSubmitting"
        @confirm="handleCredentialsSubmit"
        @close="hideCredentialsModal"
      >
        <div class="flex flex-col gap-4">
          <Input
            v-model="storeUrl"
            :label="
              $t('INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.STORE_URL.LABEL')
            "
            :placeholder="
              $t(
                'INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.STORE_URL.PLACEHOLDER'
              )
            "
          />
          <Input
            v-model="consumerKey"
            :label="
              $t(
                'INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.CONSUMER_KEY.LABEL'
              )
            "
            :placeholder="
              $t(
                'INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.CONSUMER_KEY.PLACEHOLDER'
              )
            "
          />
          <Input
            v-model="consumerSecret"
            type="password"
            :label="
              $t(
                'INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.CONSUMER_SECRET.LABEL'
              )
            "
            :placeholder="
              $t(
                'INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.CONSUMER_SECRET.PLACEHOLDER'
              )
            "
          />
          <p class="text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.CREDENTIALS.HELP') }}
          </p>
          <p v-if="formError" class="text-sm text-n-ruby-9">
            {{ formError }}
          </p>
        </div>
      </Dialog>
    </div>

    <div v-else class="flex items-center justify-center flex-1">
      <Spinner size="" color-scheme="primary" />
    </div>
  </div>
</template>
